import { useState } from 'react';

interface DropZoneProps {
  file: File | null;
  onFileSelect: (file: File | null) => void;
  onRemove: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
}

export default function DropZone({ file, onFileSelect, onRemove, fileInputRef }: DropZoneProps) {
  const [dragging, setDragging] = useState<boolean>(false);

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const droppedFile = e.dataTransfer?.files?.[0];
    if (droppedFile) onFileSelect(droppedFile);
  };

  return (
    <div
      className={`drop-zone ${dragging ? 'drop-zone-active' : ''}`}
      onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleFileDrop}
      onClick={() => fileInputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => e.key === 'Enter' && fileInputRef.current?.click()}
      aria-label="Drop files here or click to browse"
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx,.doc,.epub,.txt,.md"
        style={{ display: 'none' }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFileSelect(e.target.files?.[0] || null)}
      />

      {file ? (
        <div className="drop-zone-file">
          <span className="file-icon">📄</span>
          <span className="file-name">{file.name}</span>
          <span className="file-size">({(file.size / 1024).toFixed(0)} KB)</span>
          <button
            type="button"
            className="btn-icon"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              onRemove();
            }}
            aria-label="Remove file"
          >
            ✕
          </button>
        </div>
      ) : (
        <div className="drop-zone-prompt">
          <span className="drop-icon">⬆️</span>
          <p>Drop a file here or click to browse</p>
          <small>PDF, DOCX, EPUB, TXT — up to 10 MB</small>
        </div>
      )}
    </div>
  );
}
